import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Slide,
  IconButton,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import axios from 'axios';
import Lottie from 'lottie-react';
import botAnimation from '../assets/bot.json';
import voiceWave from '../assets/voice-wave.json';

// ==============================
const API_URL = "https://api.odonto-bot.cl";

// ===============================
const numeroATexto = (num) => {
  const unidades = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
    'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
  const decenas = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta',
    'setenta', 'ochenta', 'noventa'];
  const centenas = ['', 'cien', 'doscientos', 'trescientos', 'trescientos', 'cuatrocientos', 'quinientos',
    'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

  const seccion = (n) => {
    if (n < 20) return unidades[n];
    if (n < 100) return decenas[Math.floor(n / 10)] + (n % 10 ? ' y ' + unidades[n % 10] : '');
    if (n < 1000) return centenas[Math.floor(n / 100)] + (n % 100 ? ' ' + seccion(n % 100) : '');
    if (n < 1000000) {
      const miles = Math.floor(n / 1000);
      const resto = n % 1000;
      return (miles === 1 ? 'mil' : seccion(miles) + ' mil') + (resto ? ' ' + seccion(resto) : '');
    }
    const millones = Math.floor(n / 1000000);
    const resto = n % 1000000;
    return (millones === 1 ? 'un millón' : seccion(millones) + ' millones') + (resto ? ' ' + seccion(resto) : '');
  };

  return seccion(num);
};

// ===============================
const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [vozActiva, setVozActiva] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);

  const recognitionRef = useRef(null);
  const utteranceRef = useRef(null);
  const chatEndRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!vozActiva) {
      speechSynthesis.cancel();
    }
  }, [vozActiva]);

  const getSpanishVoice = () => {
    const voices = speechSynthesis.getVoices();
    return (
      voices.find(v => v.lang === 'es-CL') ||
      voices.find(v => v.lang === 'es-MX') ||
      voices.find(v => v.lang.startsWith('es')) ||
      voices.find(v => v.lang.startsWith('es-419'))
    );
  };

  const speak = (text) => {
    if (!vozActiva) return;

    let cleaned = text.replace(/<[^>]*>?/gm, '');
    cleaned = cleaned.replace(/\$([\d.]+)/g, (_, raw) => {
      const numeric = parseInt(raw.replace(/\./g, ''));
      return `${numeroATexto(numeric)} pesos`;
    });
    cleaned = cleaned.replace(/Odonto[\s-]?Bot/gi, 'Odonto Bót');

    const utterance = new SpeechSynthesisUtterance(cleaned);
    utteranceRef.current = utterance;

    const voice = getSpanishVoice();
    if (voice) utterance.voice = voice;

    utterance.lang = 'es-CL';
    utterance.rate = 1;

    speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.post(`${API_URL}/chat/sesion`);
        setSessionId(res.data.session_id);

        const welcome = "¡Hola! Soy Odonto-Bot, tu asistente virtual. ¿En qué puedo ayudarte hoy?";
        setMessages([{ sender: "bot", type: "text", text: welcome }]);
        speak(welcome);

      } catch (error) {
        console.error("Error creando sesión:", error);
      }
    };
    init();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || !sessionId) return;

    const newMsg = { sender: "user", type: "text", text: input };
    setMessages((prev) => [...prev, newMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/responder/responder`, {
        pregunta: newMsg.text,
      });

      const data = res.data;

      if (data.tipo === "video") {
        speechSynthesis.cancel();

        const botMsg = {
          sender: "bot",
          type: "video",
          video_id: data.video_id,
          start_time: data.start_time,
          end_time: data.end_time,
          text: "",
        };

        setMessages((prev) => [...prev, botMsg]);
        return;
      }

      if (data.tipo === "sin_resultado") {
        const mensaje = data.mensaje || "No encontré videos para esa consulta";

        const botMsg = {
          sender: "bot",
          type: "text",
          text: mensaje
        };

        setMessages((prev) => [...prev, botMsg]);
        speak(mensaje);
        return;
      }

      const respuesta = data?.respuesta || "No dispongo de información que permita responder esa pregunta.";

      const botMsg = {
        sender: "bot",
        type: "text",
        text: respuesta
      };

      setMessages((prev) => [...prev, botMsg]);
      speak(respuesta);

    } catch (error) {
      const errorMsg = "⚠️ Error al conectar con Odonto-Bot.";
      setMessages((prev) => [...prev, { sender: "bot", type: "text", text: errorMsg }]);
      speak(errorMsg);

    } finally {
      setLoading(false);
    }
  };

  const handleVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return alert("Tu navegador no soporta reconocimiento de voz");

    if (!recognitionRef.current) {
      const rec = new SpeechRecognition();
      rec.lang = "es-CL";
      rec.interimResults = false;
      rec.maxAlternatives = 1;

      rec.onstart = () => setIsListening(true);
      rec.onend = () => setIsListening(false);

      rec.onresult = (event) => {
        const text = event.results[0][0].transcript;
        setInput(text);
        setTimeout(handleSend, 150);
      };

      recognitionRef.current = rec;
    }

    if (isListening) {
      recognitionRef.current.stop();
      return;
    }

    recognitionRef.current.start();
  };

  const handleClear = () => {
    const welcome = "¡Hola! Soy Odonto-Bot, tu asistente virtual. ¿En qué puedo ayudarte hoy?";
    setMessages([{ sender: "bot", type: "text", text: welcome }]);
    speak(welcome);
  };

  const aplicarTiempoVideo = (video, startTime) => {
    const targetTime = Number(startTime || 0);

    if (!Number.isFinite(targetTime)) return;

    try {
      if (Math.abs(video.currentTime - targetTime) > 0.8) {
        video.currentTime = targetTime;
      }
    } catch (error) {
      console.error("Error posicionando video:", error);
    }
  };

  const maximizarVideo = (video) => {
    if (!video) return;

    const requestFullscreen =
      video.requestFullscreen ||
      video.webkitRequestFullscreen ||
      video.msRequestFullscreen;

    if (requestFullscreen) {
      requestFullscreen.call(video).catch?.((error) => {
        console.error("Error maximizando video:", error);
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        background: "linear-gradient(135deg, #1e3a8a, #1e40af, #3b82f6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2
      }}
    >

      {/* 🔥 LOGO FONDO */}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          width: "50%",
          height: "100%",
          opacity: 0.06,
          pointerEvents: "none",
          backgroundImage: "url('/logo-u-chile-watermark.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "50%"
        }}
      />

      <Paper elevation={10} sx={{ width: "100%", maxWidth: 700, borderRadius: 4, p: 3, backgroundColor: "rgba(255, 255, 255, 0.85)", backdropFilter: "blur(10px)" }}>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 60 }}>
              <Lottie animationData={botAnimation} loop />
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#0b488e" }}>
              Odonto-Bot, tu asistente
            </Typography>
          </Stack>

          <Tooltip title="Limpiar conversación">
            <IconButton sx={{ color: "#0b488e" }} onClick={handleClear}>
              <DeleteSweepIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ height: isMobile ? 350 : 450, overflowY: "auto", border: "1.5px solid #c4c8cf", borderRadius: 3, p: 2, backgroundColor: "#ffffffdd" }}>
          <Stack spacing={2}>
            {messages.map((msg, index) => (
              <Slide key={index} direction="up" in mountOnEnter unmountOnExit timeout={{ enter: 200 }}>
                <Box
                  sx={{
                    alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
                    backgroundColor: msg.sender === "user" ? "#ff7043" : "#26c6da",
                    color: "#fff",
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    maxWidth: "85%",
                    boxShadow: 3,
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    {msg.sender === "user" ? "Tú" : "Odonto-Bot"}
                  </Typography>

                  {msg.type === "video" ? (
  <Box sx={{ mt: 1 }}>
    <Box
      data-video-wrapper
      sx={{
        position: "relative",
        lineHeight: 0,
        isolation: "isolate",
        overflow: "visible",
      }}
    >
      <Tooltip title="Maximizar video">
        <IconButton
          aria-label="Maximizar video"
          size="small"
          onClick={(e) => {
            const video = e.currentTarget
              .closest('[data-video-wrapper]')
              ?.querySelector('video');
            maximizarVideo(video);
          }}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            color: "#fff",
            backgroundColor: "rgba(0, 0, 0, 0.48)",
            '&:hover': { backgroundColor: "rgba(0, 0, 0, 0.68)" },
          }}
        >
          <FullscreenIcon fontSize="small" />
        </IconButton>
      </Tooltip>

    <video
      key={`${msg.video_id}-${msg.start_time}-${msg.end_time}-${index}`}
      controls
      autoPlay
      playsInline
      preload="metadata"
      style={{
        width: "100%",
        borderRadius: 8,
        display: "block",
        position: "relative",
        zIndex: 1,
        pointerEvents: "auto",
      }}
      onDoubleClick={(e) => maximizarVideo(e.currentTarget)}
      onLoadedMetadata={(e) => {
        aplicarTiempoVideo(e.currentTarget, msg.start_time);
      }}
      onCanPlay={(e) => {
        aplicarTiempoVideo(e.currentTarget, msg.start_time);
      }}
    >
      <source
        src={`${API_URL}/videos/${msg.video_id}/play?start=${msg.start_time}&end=${msg.end_time}&v=${index}`}
        type="video/mp4"
      />
      Tu navegador no soporta la reproducción de video.
    </video>
    </Box>

    <Typography variant="body2" sx={{ mt: 1 }}>
      {msg.text}
    </Typography>
  </Box>
) : (
  <Typography
    variant="body2"
    dangerouslySetInnerHTML={{ __html: msg.text }}
  />
)}

                </Box>
              </Slide>
            ))}

            <div ref={chatEndRef} />

            {loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="textSecondary">
                  Procesando...
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} mt={2} alignItems="center">
          <TextField
            fullWidth
            value={input}
            placeholder="Escribe tu pregunta..."
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#c4c8cf'
                },
                '&:hover fieldset': {
                  borderColor: '#0b488e'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#93969b',
                  borderWidth: '1px'
                }
              }
            }}
          />

          <IconButton
            onClick={handleSend}
            sx={{
              backgroundColor: "#0b488e",
              color: "#fff",
              width: 50,
              height: 50,
              '&:hover': { backgroundColor: "#093a73" }
            }}
          >
            <SendIcon />
          </IconButton>

          <Tooltip title={isListening ? "Escuchando..." : "Hablar"}>
            <IconButton onClick={handleVoice}>
              <KeyboardVoiceIcon sx={{ fontSize: 32, color: "#0b488e" }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <FormControlLabel
          control={
            <Switch
              checked={vozActiva}
              onChange={(e) => setVozActiva(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#0b488e'
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#0b488e'
                }
              }}
            />
          }
          label="Voz activa"
          sx={{ mt: 2 }}
        />
      </Paper>
    </Box>
  );
};

export default ChatBot;
